/**
 * Every string this surface renders.
 *
 * The copy addresses the operator, never the reviewer: no design rationale, no
 * technical vocabulary, and no restatement of a mechanic the screen already
 * performs. The reason a shelf carries no layout control lives in the UI/UX
 * companion; the screen only carries the consequence.
 *
 * The source and self-resolving dictionaries are keyed on the **configured**
 * vocabulary's members. A member with no entry falls through to its own key
 * rather than being dropped, so a ninth source registered in the runtime
 * configuration surface appears here with no release.
 */
export const copy = {
  head: {
    title: 'الرفوف',
    lede:
      'الصفحة الرئيسية تُركَّب من قائمة رفوف مرتّبة، وهذه هي القائمة. لكلّ رفّ عنوانٌ يقرؤه الزائر، ' +
      'ومصدرٌ يأتي منه، ومرتبةٌ في الصفحة — وترتيب الصفّ هنا هو ترتيبه هناك.',
  },

  crumbs: {
    console: 'لوحة التشغيل',
    here: 'الرفوف',
  },

  surfaces: {
    PUBLIC_HOME: {
      heading: 'الصفحة العامّة',
      context:
        'ما يراه زائر بلا حساب: الفهرس ورفوفه وشجرة التصنيفات والبحث. المحجوب خلف الجدار هو صفحة العنوان ' +
        'والمشغّل والاشتراك.',
      caption: 'رفوف الصفحة العامّة، مرتّبة كما تظهر في الصفحة',
    },
    SUBSCRIBER_HOME: {
      heading: 'صفحة المشترك',
      context:
        'الصفحة التي يلقاها المشترك بعد الدخول: الرفوف نفسها بالآليّة نفسها، ومعها رفّان مصدرهما المشاهد ' +
        'نفسه. الرفّ الذي لا يعيد شيئاً يُحذف من الصفحة ولا يُعرض فارغاً، فمشترك جديد يلقى ترتيب الصفحة ' +
        'العامّة حتى يشاهد أو يحفظ.',
      caption: 'رفوف صفحة المشترك، مرتّبة كما تظهر في الصفحة',
    },
  } as const,

  add: 'أضف رفّاً',

  columns: {
    order: 'الترتيب',
    shelf: 'الرفّ',
    source: 'المصدر',
    /** Visually hidden — the actions column names itself for a screen reader. */
    actions: 'أفعال الرفّ',
  },

  row: {
    moveUp: (name: string) => `انقل «${name}» صفّاً إلى الأعلى`,
    moveDown: (name: string) => `انقل «${name}» صفّاً إلى الأسفل`,
    menu: (name: string) => `أفعال رفّ «${name}»`,
    edit: 'حرّر الرفّ',
    delete: 'احذف الرفّ',
    /** Announced politely after a move. The position arrives already formatted. */
    moved: (name: string, position: string) => `«${name}» صار في المرتبة ${position}`,
  },

  /**
   * The second line under a shelf's title — where «عرض الكلّ» leads. Derived
   * from the row's own see-all target and its source; the catalog makes the
   * link correspond to the source, so it is read rather than authored.
   */
  seeAll: {
    category: '«عرض الكلّ» إلى صفحة التصنيف',
    tag: '«عرض الكلّ» إلى صفحة الوسم',
    recency: '«عرض الكلّ» إلى نتائج البحث مرتّبةً بالأحدث',
    popularity: '«عرض الكلّ» إلى نتائج البحث مرتّبةً بالأكثر مشاهدة',
    default: '«عرض الكلّ» إلى نتائج البحث',
    none: 'بلا رابط «عرض الكلّ»',
    /** A hero shelf *is* the top of the page, so there is nowhere to send the reader. */
    heroNone: 'بلا رابط «عرض الكلّ» — العارض هو أعلى الصفحة نفسها',
  },

  viewerScoped: {
    note: 'مصدره المشاهد نفسه',
    badge: 'يظهر عند وجود عناصر',
  },

  /** Source wording. A configured member with no entry renders as its own key. */
  sources: {
    category: 'تصنيف',
    tag: 'وسم',
    recency: 'الأحدث',
    popularity: 'الأكثر مشاهدة',
    related_to_current_item: 'مرتبط بالعنوان المعروض',
    editorial_hand_pick: 'اختيار تحريري',
    continue_watching: 'متابعة المشاهدة',
    saved_list: 'قائمة محفوظة',
  },

  /** The second line of a source cell where the source takes no parameter. */
  selfResolving: {
    recency: 'يحلّ بنفسه — بلا تحديد',
    popularity: 'يحلّ بنفسه — بلا تحديد',
    related_to_current_item: 'يحلّ من العنوان المعروض — بلا تحديد',
    continue_watching: 'يحلّ من سجلّ المشاهد — بلا تحديد',
    saved_list: 'يحلّ من قائمة المشاهد — بلا تحديد',
    default: 'يحلّ بنفسه — بلا تحديد',
  },

  /** A hand-picked set is summarised by its count; eight titles in a cell are not read. */
  handPicked: (count: string) => `${count} عناوين مختارة`,

  dialog: {
    headingEdit: 'حرّر الرفّ',
    headingCreate: 'أضف رفّاً',
    title: 'عنوان الرفّ',
    titleHint: 'هذا هو النصّ الذي يقرؤه الزائر فوق الرفّ في الصفحة الرئيسية، لا اسم داخليّ.',
    source: 'المصدر',
    relatedHint: 'هذا المصدر يخصّ صفحة العنوان، لا الصفحة الرئيسية.',
    noParam: 'هذا المصدر يحلّ بنفسه — لا تحديد يُكتب له.',
    save: 'احفظ',
    cancel: 'ألغِ',

    params: {
      CATEGORY: {
        label: 'التصنيف',
        searchLabel: 'ابحث في شجرة التصنيفات',
        searchPlaceholder: 'ابحث في شجرة التصنيفات…',
        hint: 'المفردات من شجرة التصنيفات وتُدار في صفحة التصنيفات — تُختار هنا ولا تُنشأ.',
      },
      TAG: {
        label: 'الوسم',
        searchLabel: 'ابحث في الوسوم',
        searchPlaceholder: 'ابحث في الوسوم…',
        hint: 'الوسوم تُكتب على العناوين وتُدار في صفحة الوسوم — تُختار هنا ولا تُنشأ.',
      },
      CONTENT_ITEM_SET: {
        label: 'العناوين المختارة',
        searchLabel: 'ابحث في الفهرس عن عنوان',
        searchPlaceholder: 'ابحث في الفهرس عن عنوان…',
        hint: 'الترتيب هنا هو ترتيب العرض على الصفحة الرئيسية.',
        /**
         * The reach of the search, stated where the search is.
         *
         * The catalogue outgrows one page, the picker searches only what it
         * loaded, and «لا نتيجة تطابق البحث» for a title that exists is a wrong
         * answer given silently. This line is what makes the limit visible.
         */
        searchScope: (loaded: string, total: string) => `البحث يجري ضمن أحدث ${loaded} عنواناً من ${total}.`,
      },
    },

    /**
     * A title's publish state, shown beside its name in the picker.
     *
     * `PUBLISHED` carries no note: it is the ordinary case, and a badge on every
     * row is a badge nobody reads. The other three are what an operator needs to
     * see before binding a public shelf to them — the landing page drops a card
     * whose title is not public, so the shelf shrinks at render with nothing on
     * screen explaining why.
     */
    publishState: {
      DRAFT: 'مسودة',
      UNPUBLISHED: 'غير منشور',
    },

    picker: {
      resultCount: (count: string) => `${count} نتيجة`,
      noResults: 'لا نتيجة تطابق البحث',
      remove: (label: string) => `انزع «${label}»`,
    },
  },

  /** The curated chip strip — the third section of this page. */
  strip: {
    heading: 'شريط الوسوم',
    context:
      'الشريط الذي يعلو القوائم في الصفحة العامّة وصفحة المشترك. الوسوم تُكتب على العناوين وتُدار في ' +
      'صفحة الوسوم؛ ما يُختار هنا هو أيّها يظهر في الشريط وبأيّ ترتيب.',
    /** Said before the picker, because it is the state the operator is in until they curate. */
    uncurated: (count: string) =>
      `لا تحديد بعد — يعرض الشريط المفردات كاملة، وهي ${count} وسماً. اختر ما يظهر منها أدناه.`,
    curated: (count: string) => `الشريط يعرض ${count} وسماً مختاراً، بالترتيب أدناه.`,
    pickerLabel: 'وسوم الشريط',
    hint: 'الترتيب هنا هو ترتيب الظهور. الوسم يُضاف في آخر الشريط، ويُنزع بزرّ النزع عليه.',
    /** What an empty save means. Stated on the control that performs it. */
    clearHint: 'حفظ الشريط فارغاً يُلغي التحديد ويعيد عرض المفردات كاملة.',
    save: 'احفظ الشريط',
    revert: 'أعِد ما هو محفوظ',
    saved: 'حُفظ شريط الوسوم.',
    empty: 'لا وسوم في الفهرس بعد.',
    loading: 'جارٍ تحميل شريط الوسوم',
  },

  /**
   * A confirmation names the item and, where the item is not unique, its
   * surface too: a shelf title can appear in both home lists, and deleting one
   * is not deleting the other.
   */
  confirm: {
    heading: (name: string, surface: string) => `حذف رفّ «${name}» من ${surface} نهائياً؟`,
    body:
      'يختفي الرفّ من الصفحة فوراً. العناوين التي كان يعرضها تبقى في الفهرس كما هي — الرفّ ترتيبٌ لها ' +
      'لا مكانٌ تُخزَّن فيه.',
    commit: 'احذف الرفّ',
  },

  /** Field-level validation, keyed on the schema's own codes. */
  validation: {
    'title.required': 'اكتب عنواناً للرفّ.',
    'param.required': 'اختر ما يُحلّ عليه هذا المصدر.',
    'param.forbidden': 'هذا المصدر يحلّ بنفسه — انزع التحديد.',
  },

  toast: {
    saved: 'حُفظ الرفّ.',
    created: 'أُضيف الرفّ.',
    deleted: 'حُذف الرفّ.',
    reordered: 'حُفظ الترتيب الجديد.',
  },

  states: {
    loading: 'جارٍ تحميل الرفوف',
    empty: 'لا رفوف في هذه الصفحة بعد.',
    /** Never silent truncation: how many rows are shown against how many exist. */
    truncation: (shown: string, total: string) => `تُعرض ${shown} من ${total} رفّاً.`,
  },
} as const;
